import { inject, Injectable } from "@angular/core";
import { AccountMarketplace, AccountType, AdStats } from "@front/m19-api-client";
import { Metric, SupportedAccountType } from "@front/m19-metrics";
import { AdStatsEx, CatalogBrandEx, InventoryStats, OrderStats, ProductEx } from "@front/m19-models";
import {
  emptyAdStatEx,
  MapUtils,
  mergeAllStats,
  mergeSeveralDates,
  mergeVendorAdStatsExByAsin,
} from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { convertToAdStats } from "@m19-board/product-analysis/product-analysis.component";
import { Service360 } from "@m19-board/services/360.service";
import { AdType } from "@m19-board/shared/compact-filter/compact-filter.component";
import { hasAdTypes, hasAsinFilters, WidgetConfig } from "@m19-board/widget/widget-config";
import { DataTableDataset, ViewBy, WidgetType } from "@m19-board/widget/widget.model";
import { combineLatest, map, merge, Observable, of, shareReplay, Subject, switchMap } from "rxjs";
import { tap } from "rxjs/operators";

export const AllA360DatatableViewBy = [
  ViewBy.ASINS,
  ViewBy.PARENT_ASINS,
  ViewBy.PRODUCT_BRANDS,
  ViewBy.PRODUCT_TAG_1,
  ViewBy.PRODUCT_TAG_2,
  ViewBy.BRANDED_TRAFFIC,
  ViewBy.STRATEGIES,
  ViewBy.PROFIT_DETAILS,
] as const;
// create union type from array
export type A360DatatableViewBy = (typeof AllA360DatatableViewBy)[number];

export const A360DatatableConfig: Record<
  A360DatatableViewBy,
  {
    sortByOption: boolean;
    topOption: boolean;
    filterOption: boolean;
    adTypeFilterOption: boolean;
    dataset: DataTableDataset;
    supportedAccountType: SupportedAccountType;
  }
> = {
  [ViewBy.ASINS]: {
    sortByOption: true,
    topOption: true,
    filterOption: true,
    adTypeFilterOption: true,
    dataset: DataTableDataset.BestSellerAsins,
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [ViewBy.PARENT_ASINS]: {
    sortByOption: true,
    topOption: false,
    filterOption: true,
    adTypeFilterOption: true,
    dataset: DataTableDataset.ParentAsins,
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [ViewBy.PRODUCT_BRANDS]: {
    sortByOption: true,
    topOption: false,
    filterOption: true,
    adTypeFilterOption: false,
    dataset: DataTableDataset.ProductBrands,
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [ViewBy.PRODUCT_TAG_1]: {
    sortByOption: true,
    topOption: false,
    filterOption: true,
    adTypeFilterOption: true,
    dataset: DataTableDataset.CustomFieldStats,
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [ViewBy.PRODUCT_TAG_2]: {
    sortByOption: true,
    topOption: false,
    filterOption: true,
    adTypeFilterOption: true,
    dataset: DataTableDataset.CustomFieldStats,
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [ViewBy.BRANDED_TRAFFIC]: {
    sortByOption: true,
    topOption: false,
    adTypeFilterOption: false,
    filterOption: false,
    dataset: DataTableDataset.BrandingTrafficTable,
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [ViewBy.STRATEGIES]: {
    sortByOption: true,
    topOption: false,
    adTypeFilterOption: false,
    filterOption: false,
    dataset: DataTableDataset.StrategyStats,
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [ViewBy.PROFIT_DETAILS]: {
    sortByOption: false,
    topOption: false,
    adTypeFilterOption: false,
    filterOption: true,
    dataset: DataTableDataset.ProfitDetails,
    supportedAccountType: SupportedAccountType.SELLER,
  },
};

export type AdStatsInventoryStats = AdStatsEx &
  InventoryStats & { customField?: string; brand?: string; parentAsin?: string };

export type A360DataTableDataSetDescription = {
  correspondingViewBy: A360DatatableViewBy;
};

@Injectable({ providedIn: "root" })
export class Account360Service extends Service360 {
  private readonly translocoService = inject(TranslocoService);

  readonly dailyCampaignStats$: Observable<AdStatsEx[]>;
  readonly previousDailyCampaignStats$: Observable<AdStatsEx[]>;
  readonly last12WeeksGlobalData$: Observable<Map<string, AdStats>>;
  readonly last12WeeksData$: Observable<AdStatsEx[]>;
  readonly brandingTraffic$: Observable<AdStatsEx[]>;
  readonly previousBrandingTraffic$: Observable<AdStatsEx[]>;
  readonly dailyVendorInventoryStats$: Observable<AdStatsEx[]>;
  readonly vendorInventoryStatsPerAsin$: Observable<AdStatsEx[]>;
  readonly previousVendorInventoryStatsPerAsin$: Observable<AdStatsEx[]>;
  readonly previousDailyVendorInventoryStats$: Observable<AdStatsEx[]>;
  readonly aggregatedVendorInventoryStats$: Observable<AdStatsEx>;
  readonly previousAggregatedVendorInventoryStats$: Observable<AdStatsEx>;

  constructor() {
    super();

    this.dailyVendorInventoryStats$ = combineLatest([
      this.accountSelection.singleAccountMarketplaceSelection$,
      this.userSelectionService.dateRange$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, dr, currency]) =>
        am.accountType == AccountType.VENDOR
          ? this.statsService.getDailyVendorInventory(
              am.accountId,
              am.marketplace,
              currency,
              dr[0],
              dr[1],
              !!am.useSourcingMetrics,
            )
          : of([]),
      ),
      shareReplay(1),
    );
    this.previousDailyVendorInventoryStats$ = combineLatest([
      this.accountSelection.singleAccountMarketplaceSelection$,
      this.userSelectionService.periodComparison$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, pc, currency]) => {
        if (am.accountType != AccountType.VENDOR || !pc || !pc.period) {
          return of([]);
        }
        return this.statsService.getDailyVendorInventory(
          am.accountId,
          am.marketplace,
          currency,
          pc.period[0],
          pc.period[1],
          !!am.useSourcingMetrics,
        );
      }),
      shareReplay(1),
    );
    this.vendorInventoryStatsPerAsin$ = this.dailyVendorInventoryStats$.pipe(
      map((data) => mergeVendorAdStatsExByAsin(data)),
      shareReplay(1),
    );
    this.previousVendorInventoryStatsPerAsin$ = this.previousDailyVendorInventoryStats$.pipe(
      map((data) => mergeVendorAdStatsExByAsin(data)),
      shareReplay(1),
    );
    this.aggregatedVendorInventoryStats$ = this.dailyVendorInventoryStats$.pipe(
      map((data) => data.reduce((acc, curr) => mergeSeveralDates(acc, curr), emptyAdStatEx())),
      shareReplay(1),
    );
    this.previousAggregatedVendorInventoryStats$ = this.previousDailyVendorInventoryStats$.pipe(
      map((data) => data.reduce((acc, curr) => mergeSeveralDates(acc, curr), emptyAdStatEx())),
      shareReplay(1),
    );

    const dailyProductStats = this.getDailyProductStats();
    const dailyProductStatsPreviousPeriod = this.getDailyProductStats(true);
    this.dailyProductStats$ = dailyProductStats.data$.pipe(shareReplay(1));
    this.previousDailyProductStats$ = dailyProductStatsPreviousPeriod.data$.pipe(shareReplay(1));
    this.aggregatedProductStats$ = this.dailyProductStats$.pipe(
      map((data) => this.aggregateProductStats(data)),
      shareReplay(1),
    );
    this.aggregatedProductStatsPreviousPeriod$ = this.previousDailyProductStats$.pipe(
      map((data) => this.aggregateProductStats(data)),
      shareReplay(1),
    );
    this.productStatsPerAsin$ = this.getProductStatsPerAsin([]).data$.pipe(shareReplay(1));
    this.previousProductStatsPerAsin$ = this.getProductStatsPerAsin([], true).data$.pipe(shareReplay(1));
    this.productStatsReload$ = merge(dailyProductStats.reload$, dailyProductStatsPreviousPeriod.reload$);
    this.profitDetails$ = this.aggregatedProductStats$.pipe(
      map((data) => this.buildProfitDetails(data as OrderStats)),
      shareReplay(1),
    );

    this.dailyCampaignStats$ = combineLatest([
      this.accountSelection.singleAccountMarketplaceSelection$,
      this.userSelectionService.dateRange$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, dr, currency]) =>
        combineLatest([
          this.statsService.getDailyCampaignStats(am.accountId, am.marketplace, currency, dr[0], dr[1]),
          am.dspAdvertiserId
            ? this.statsService.getDailyDspCampaignStats(
                am.accountId,
                am.marketplace,
                am.dspAdvertiserId,
                currency,
                dr[0],
                dr[1],
              )
            : of([]),
        ]),
      ),
      map(([data, dspStats]) => [...data, ...dspStats]),
      shareReplay(1),
    );

    this.previousDailyCampaignStats$ = combineLatest([
      this.accountSelection.singleAccountMarketplaceSelection$,
      this.userSelectionService.periodComparison$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, pc, currency]) => {
        if (!pc?.period) {
          return of([]);
        }
        return combineLatest([
          this.statsService.getDailyCampaignStats(am.accountId, am.marketplace, currency, pc.period[0], pc.period[1]),
          am.dspAdvertiserId
            ? this.statsService.getDailyDspCampaignStats(
                am.accountId,
                am.marketplace,
                am.dspAdvertiserId!,
                currency,
                pc.period[0],
                pc.period[1],
              )
            : of([]),
        ]).pipe(map(([data, dspStats]) => [...data, ...dspStats]));
      }),
      shareReplay(1),
    );

    // last 12 weeks stats
    // (AdStatsEx)[]
    this.last12WeeksData$ = combineLatest([
      this.accountSelection.singleAccountMarketplaceSelection$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, currency]) =>
        this.statsService.getLast12WeeksAsinStats(am.accountId, am.marketplace, currency, am.useSourcingMetrics),
      ),
      shareReplay(1),
    );

    this.last12WeeksGlobalData$ = this.get12WeeksGlobalStats().pipe(shareReplay(1));

    this.brandingTraffic$ = this.getBrandingTraffic(
      combineLatest([
        this.accountSelection.singleAccountMarketplaceSelection$,
        this.userSelectionService.dateRange$,
        this.userSelectionService.selectedCurrency$,
      ]).pipe(
        switchMap(([am, [minDate, maxDate], currency]) =>
          this.statsService.getDailyBrandAsinStats(am.accountId, am.marketplace, currency, minDate, maxDate),
        ),
      ),
    );
    this.previousBrandingTraffic$ = this.getBrandingTraffic(
      combineLatest([
        this.accountSelection.singleAccountMarketplaceSelection$,
        this.userSelectionService.periodComparison$,
        this.userSelectionService.selectedCurrency$,
      ]).pipe(
        switchMap(([am, periodComparison, currency]) => {
          if (periodComparison && periodComparison.period) {
            this.statsService.getDailyBrandAsinStats(
              am.accountId,
              am.marketplace,
              currency,
              periodComparison.period[0],
              periodComparison.period[1],
            );
          }
          return of([]);
        }),
      ),
    );
  }

  private getBrandingTraffic(adStats$: Observable<AdStatsEx[]>) {
    return combineLatest([
      adStats$,
      this.accountSelection.singleAccountMarketplaceSelection$.pipe(
        switchMap((am: AccountMarketplace) => this.catalogBrandService.getCatalogBrands(am.accountId, am.marketplace)),
      ),
    ]).pipe(
      map(([data, brands]) => {
        const brandIdMap = brands.reduce((acc, b) => {
          acc.set(b.brandId!, b);
          return acc;
        }, new Map<number, CatalogBrandEx>());
        return data.map((d) => {
          const catalogBrand = brandIdMap.get(d.brandId!);
          return {
            ...d,
            catalogBrand,
            catalogBrandName:
              catalogBrand?.brandAlias ?? this.translocoService.translate("dsp-stats.dspCampaignDeliveryStatus_OTHER"),
          };
        });
      }),
      shareReplay(1),
    );
  }

  public getDailyProductStats(
    previousPeriod = false,
    config?: WidgetConfig<WidgetType>,
  ): {
    data$: Observable<AdStats[]>;
    reload$: Observable<void>;
  } {
    const reload$ = new Subject<void>();
    return {
      reload$,
      data$: combineLatest([
        this.accountSelection.singleAccountMarketplaceSelection$,
        previousPeriod
          ? this.userSelectionService.periodComparison$.pipe(map((pc) => pc?.period))
          : this.userSelectionService.dateRange$,
        config && hasAsinFilters(config) ? this.getFilteredAsins(config) : of(undefined),
        this.userSelectionService.selectedCurrency$,
      ]).pipe(
        tap(([_am, dr, _]) => {
          if (dr !== undefined) {
            reload$.next();
          }
        }),
        switchMap(([am, dr, products, currency]) => {
          if (dr === undefined) {
            return of([]);
          }
          return this.statsService.getDailyProductStats(
            am.accountId,
            am.marketplace,
            currency,
            dr[0],
            dr[1],
            am.useSourcingMetrics ?? false,
            am.dspAdvertiserId,
            products?.filter((p) => p.asin)?.map((p) => p.asin!),
          );
        }),
        map((data) => convertToAdStats(data, hasAdTypes(config) ? config.adTypes! : [])),
      ),
    };
  }

  public getProductStatsPerAsin(
    adTypes: AdType[],
    previousPeriod = false,
  ): {
    data$: Observable<AdStats[]>;
    reload$: Observable<void>;
  } {
    const reload$ = new Subject<void>();
    return {
      reload$,
      data$: combineLatest([
        this.accountSelection.singleAccountMarketplaceSelection$,
        previousPeriod
          ? this.userSelectionService.periodComparison$.pipe(map((pc) => pc?.period))
          : this.userSelectionService.dateRange$,
        this.userSelectionService.selectedCurrency$,
      ]).pipe(
        tap(([_am, dr]) => {
          if (dr !== undefined) {
            reload$.next();
          }
        }),
        switchMap(([am, dr, currency]) => {
          if (dr === undefined) {
            return of([]);
          }
          return this.statsService.getAsinProductStats(
            am.accountId,
            am.marketplace,
            currency,
            dr[0],
            dr[1],
            am.useSourcingMetrics ?? false,
            am.dspAdvertiserId,
          );
        }),
        map((data) => convertToAdStats(data, adTypes)),
      ),
    };
  }

  public getDataPerProductField(
    config: WidgetConfig<WidgetType>,
    fieldGetter: (p: ProductEx) => string,
    field: keyof AdStatsInventoryStats,
    sortByMetric: Metric<AdStatsEx>,
    sortOrder: "asc" | "desc",
    previousPeriod = false,
  ): Observable<AdStatsInventoryStats[]> {
    return combineLatest([
      this.filterStats(
        previousPeriod
          ? hasAdTypes(config)
            ? this.getProductStatsPerAsin(config.adTypes!, true).data$
            : this.previousProductStatsPerAsin$
          : hasAdTypes(config)
            ? this.getProductStatsPerAsin(config.adTypes!).data$
            : this.productStatsPerAsin$,
        config,
      ),
      this.filterStats(
        previousPeriod ? this.previousVendorInventoryStatsPerAsin$ : this.vendorInventoryStatsPerAsin$,
        config,
      ),
      this.catalog$,
    ]).pipe(
      map(([adStats, vendorInventoryStats, catalog]) => {
        // aggregate stats by asin
        const adStatsByField: Map<string, AdStatsEx> = MapUtils.sumBy(
          adStats as (OrderStats & AdStatsEx)[],
          (x) => fieldGetter(catalog.asinOffers.get(x.asin!)!),
          mergeAllStats,
        );
        const inventoryStatsByField: Map<string, AdStatsEx> = MapUtils.sumBy(
          vendorInventoryStats,
          (x) => fieldGetter(catalog.asinOffers.get(x.asin!)!),
          mergeSeveralDates,
        );
        // merge adstats with inventory stats
        const mergedStats: AdStatsInventoryStats[] = [];
        for (const fieldKey of adStatsByField.keys()) {
          const adStat = adStatsByField.get(fieldKey);
          const inventoryStat = inventoryStatsByField.get(fieldKey);
          mergedStats.push({
            ...inventoryStat,
            ...adStat,
            [field]: fieldKey ?? "N/A",
          } as AdStatsInventoryStats);
        }
        // sort by metric set in config
        return mergedStats.sort(
          (a, b) => (sortOrder == "desc" ? 1 : -1) * (sortByMetric.value(b)! - sortByMetric.value(a)!),
        );
      }),
    );
  }

  public get12WeeksGlobalStats(config?: WidgetConfig<WidgetType>): Observable<Map<string, AdStats>> {
    return combineLatest([
      this.accountSelection.singleAccountMarketplaceSelection$,
      config && hasAsinFilters(config) ? this.getFilteredAsins(config) : of(undefined),
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, products, currency]) => {
        return this.statsService.getWeeklyProductStats(
          am.accountId,
          am.marketplace,
          currency,
          am.useSourcingMetrics ?? false,
          am.dspAdvertiserId,
          products?.filter((p) => p.asin)?.map((p) => p.asin!),
        );
      }),
      map((arr) => {
        const map = new Map<string, AdStats>();
        const adStats = convertToAdStats(arr, hasAdTypes(config) ? config.adTypes! : []);
        for (let i = 0; i < arr.length; i++) {
          map.set(arr[i].date!, adStats[i]);
        }
        return map;
      }),
    );
  }

  private aggregateProductStats(stats: AdStats[]): AdStats {
    return stats.reduce(
      (acc, curr) => mergeAllStats(acc as OrderStats & AdStatsEx, curr as OrderStats & AdStatsEx),
      {} as OrderStats & AdStatsEx,
    );
  }

  public getAggregatedProductStats(
    previousPeriod = false,
    config: WidgetConfig<WidgetType>,
  ): {
    data$: Observable<AdStats>;
    reload$: Observable<void>;
  } {
    const dailyProductStats = this.getDailyProductStats(previousPeriod, config);
    return {
      data$: dailyProductStats.data$.pipe(map((data) => this.aggregateProductStats(data))),
      reload$: dailyProductStats.reload$,
    };
  }
}
